import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeftIcon,
  UserGroupIcon,
  AcademicCapIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  UserIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { H1, H2, Body, Card, Button, Badge, Spinner } from '../components/ui';
import { Input } from '../components/forms';
import { CreateObservationModal } from '../components/observations';
import { useModal } from '../stores/uiStore';
import { useClassroom, useClassroomStats } from '../hooks/api/useClassrooms';
import { useChildrenByClassroom } from '../hooks/api/useChildren';

const ClassroomDetail: React.FC = () => {
  const { classroomId } = useParams<{ classroomId: string }>();
  const navigate = useNavigate();
  const createObservationModal = useModal('create-observation');
  const [searchTerm, setSearchTerm] = useState('');

  const { data: classroom, isLoading: classroomLoading, error: classroomError } = useClassroom(classroomId || '', !!classroomId);
  const { data: children = [], isLoading: childrenLoading } = useChildrenByClassroom(classroomId || '', !!classroomId);
  const { data: stats } = useClassroomStats(classroomId || '', !!classroomId);

  const handleViewChild = (childId: string) => {
    navigate(`/children/${childId}`);
  };

  const handleAddObservation = (childId: string) => {
    createObservationModal.open({ childId });
  };

  // Filter children by search
  const filteredChildren = children.filter(child => {
    const fullName = `${child.name} ${child.surname}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  if (classroomLoading) {
    return (
      <div className="max-w-7xl mx-auto flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (classroomError || !classroom) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <AcademicCapIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <H2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Classroom not found
          </H2>
          <Body className="text-gray-600 dark:text-gray-400 mb-4">
            The classroom you're looking for doesn't exist or you don't have access.
          </Body>
          <Link to="/classrooms">
            <Button variant="primary">Back to Classrooms</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-8"
      >
        {/* Back Button */}
        <div className="mb-4">
          <Link to="/classrooms">
            <Button variant="ghost" size="sm" className="flex items-center">
              <ChevronLeftIcon className="w-4 h-4 mr-2" />
              Back to Classrooms
            </Button>
          </Link>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <AcademicCapIcon className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <H1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {classroom.name}
              </H1>
              <div className="flex items-center space-x-3 mt-1">
                <Badge variant={classroom.is_active ? 'success' : 'warning'} size="sm">
                  {classroom.is_active ? 'Active' : 'Inactive'}
                </Badge>
                {classroom.code && (
                  <Badge variant="secondary" size="sm">{classroom.code}</Badge>
                )}
                {classroom.capacity && (
                  <Body className="text-sm text-gray-500 dark:text-gray-400">
                    Capacity: {classroom.capacity}
                  </Body>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3 mt-4 md:mt-0">
            <Button
              variant="primary"
              size="sm"
              onClick={() => createObservationModal.open()}
            >
              Add Observation
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="p-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <UserGroupIcon className="w-5 h-5 text-primary-500" />
          </div>
          <H2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {stats?.total_children ?? children.length}
          </H2>
          <Body className="text-sm text-gray-600 dark:text-gray-400">Children</Body>
        </Card>
        <Card className="p-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <UserGroupIcon className="w-5 h-5 text-green-500" />
          </div>
          <H2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {stats?.active_children ?? '—'}
          </H2>
          <Body className="text-sm text-gray-600 dark:text-gray-400">Active</Body>
        </Card>
        <Card className="p-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <ClipboardDocumentListIcon className="w-5 h-5 text-blue-500" />
          </div>
          <H2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {stats?.total_observations ?? '—'}
          </H2>
          <Body className="text-sm text-gray-600 dark:text-gray-400">Observations</Body>
        </Card>
        <Card className="p-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <ChartBarIcon className="w-5 h-5 text-yellow-500" />
          </div>
          <H2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {stats?.total_assessments ?? '—'}
          </H2>
          <Body className="text-sm text-gray-600 dark:text-gray-400">Assessments</Body>
        </Card>
      </div>

      {/* Children Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <H2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Children ({filteredChildren.length})
          </H2>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search children..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {childrenLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : filteredChildren.length === 0 ? (
          <div className="text-center py-12">
            <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <H2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No children found
            </H2>
            <Body className="text-gray-600 dark:text-gray-400">
              {searchTerm
                ? `No children match "${searchTerm}"`
                : 'No children enrolled in this classroom yet.'}
            </Body>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredChildren.map((child, index) => (
              <motion.div
                key={child.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card className="p-4 hover:shadow-lg transition-shadow duration-200">
                  <div className="flex items-center mb-3">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-full mr-3">
                      <UserIcon className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <H2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {child.name} {child.surname}
                      </H2>
                      <Body className="text-sm text-gray-600 dark:text-gray-400">
                        Age {child.age} {child.gender ? `· ${child.gender === 'M' ? 'Male' : child.gender === 'F' ? 'Female' : 'Other'}` : ''}
                      </Body>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewChild(child.id)}
                      className="flex items-center flex-1"
                    >
                      <EyeIcon className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleAddObservation(child.id)}
                      className="flex items-center flex-1"
                    >
                      <ClipboardDocumentListIcon className="w-4 h-4 mr-1" />
                      Observe
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </Card>

      {/* Modals */}
      <CreateObservationModal />
    </div>
  );
};

export default ClassroomDetail;
